import { useCallback, useState } from "react";
import { Swords, Loader2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { generatePrep } from "@/lib/scoutApi";
import { usePollCandidate } from "@/hooks/usePollCandidate";
import { extractApiError } from "@/lib/api";

const Section = ({ title, children }) => (
  <div className="mt-4">
    <div className="text-[10px] uppercase tracking-wider text-[var(--accent)] mb-2">{title}</div>
    {children}
  </div>
);

const Sheet = ({ s }) => (
  <div data-testid="scout-prep-sheet" className="mt-3 border border-slate-200 bg-slate-50 p-4 text-sm">
    {s.thesis && <p className="italic text-[#0A192F]">{s.thesis}</p>}
    <Section title="Steel-man — their strongest case">
      <ol className="space-y-3 list-decimal pl-5">
        {s.steelman.map((i) => (
          <li key={i.claim}>
            <div className="font-medium text-[#0A192F]">{i.claim}</div>
            <p className="text-slate-700 mt-0.5">{i.strongest_case}</p>
            {i.evidence && <p className="text-xs text-slate-500 mt-0.5">Evidence: {i.evidence}</p>}
            {i.source_url && (
              <a href={i.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:underline">
                source <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </li>
        ))}
      </ol>
    </Section>
    <Section title="Vic's counter-questions">
      <ol className="space-y-2 list-decimal pl-5">
        {s.counter_questions.map((q) => (
          <li key={q.question_id || q.question}>
            <div className="text-[#0A192F]">
              {q.question}
              {q.source === "listener" && <span data-testid="scout-prep-listener-tag" className="ml-2 align-middle text-[10px] uppercase tracking-wider border border-[var(--accent)] text-[var(--accent)] px-1.5 py-0.5">Listener</span>}
            </div>
            {q.why_it_lands && <div className="text-xs text-slate-500">{q.why_it_lands}</div>}
          </li>
        ))}
      </ol>
    </Section>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Section title="Traps to avoid">
        <ul className="space-y-1 list-disc pl-5 text-slate-700">{s.traps_to_avoid.map((t) => <li key={t}>{t}</li>)}</ul>
      </Section>
      <Section title="Common ground">
        <ul className="space-y-1 list-disc pl-5 text-slate-700">{s.common_ground.map((t) => <li key={t}>{t}</li>)}</ul>
      </Section>
    </div>
    {s.receipts?.length > 0 && (
      <Section title="Receipts">
        <ul className="space-y-1">
          {s.receipts.map((r) => (
            <li key={r.quote} className="text-slate-700">
              &ldquo;{r.quote}&rdquo;{" "}
              {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--accent)] hover:underline">source</a>}
            </li>
          ))}
        </ul>
      </Section>
    )}
  </div>
);

export const DebatePrep = ({ c, onChange }) => {
  const [busy, setBusy] = useState(c.prep_status === "building");
  const [open, setOpen] = useState(false);

  const onDone = useCallback(
    (fresh) => {
      setBusy(false);
      onChange(fresh);
      if (fresh.prep_status === "done") {
        setOpen(true);
        toast.success("Debate prep ready");
      } else if (fresh.prep_error) {
        toast.error(fresh.prep_error);
      }
    },
    [onChange]
  );
  usePollCandidate(c.id, busy, "prep_status", onDone);

  const build = async () => {
    try {
      await generatePrep(c.id);
      setBusy(true);
      toast("Researching their strongest arguments — usually 1–3 minutes.");
    } catch (err) {
      toast.error(extractApiError(err, "Could not start prep sheet."));
    }
  };

  return (
    <div className="mt-4" data-testid="scout-prep">
      {c.prep_sheet ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" data-testid="scout-prep-toggle" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 text-xs font-medium text-[#0A192F] hover:text-[var(--accent)]">
              <Swords className="w-3.5 h-3.5 text-[var(--accent)]" /> Debate prep sheet {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button type="button" onClick={build} disabled={busy} className="text-xs text-slate-500 hover:text-[#0A192F]">
              {busy ? "Rebuilding…" : "Rebuild"}
            </button>
          </div>
          {open && <Sheet s={c.prep_sheet} />}
        </>
      ) : (
        <button
          type="button"
          data-testid="scout-prep-generate"
          onClick={build}
          disabled={busy}
          className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0A192F] disabled:opacity-50 px-3 py-2 text-xs font-medium transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Swords className="w-3.5 h-3.5" />}
          {busy ? "Researching their strongest arguments…" : "Build debate prep (steel-man + counters)"}
        </button>
      )}
    </div>
  );
};

export default DebatePrep;
